import React, { Component } from "react";
import PropTypes from "prop-types";
import Fuse from "fuse.js";
import CartContext from "./CartContext";
import { MessageBox } from "./MessageBox";
import {
  Table,
  FormControl,
  Button,
  Spinner,
  Row,
  Col,
  Stack,
  Form,
  ButtonToolbar,
} from "react-bootstrap";
import { BiRefresh } from "react-icons/bi";

/**
 * SupplierPayments.jsx
 *
 * Class component that:
 *  - Fetches payment transactions from ProductPurchase endpoints (payments)
 *  - Fetches order metadata from SupplierPurchaseOrderDelivered (for mapping purchaseId -> orderNo / supplier)
 *  - Shows only these columns: Supplier name, Received Amount, Received Date, PaymentTransactionId, PaymentMethod, Purchase Order
 *  - If logged-in user is a supplier, only their supplier's data is fetched/displayed (defense-in-depth)
 *  - Uses process.env.REACT_APP_API for base URL (no props.apiBase)
 */

export default class SupplierPayments extends Component {
  static contextType = CartContext;

  static propTypes = {
    storeId: PropTypes.string,
    supplierId: PropTypes.string,
    pageSize: PropTypes.number,
    fetchUrlOverride: PropTypes.string,
  };

  static defaultProps = { pageSize: 20, fetchUrlOverride: "" };

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      payments: [], // normalized payments from ProductPurchase endpoints
      ordersMap: {}, // purchaseId -> order metadata
      filtered: [],
      search: "",
      fromDate: "",
      toDate: "",
      page: 1,
      pageSize: this.props.pageSize,
      suppliers: [],
      selectedSupplierId: "",
      lastFetchedAt: null,
      msgboxOpen: false,
      msgboxTitle: "",
      msgboxMessage: "",
    };
  }

  // helper to determine if current user is a supplier and their supplier id
  getSupplierUserContext() {
    const ctx = this.context || {};
    const user = ctx.userData || {};
    const roles = ctx.UserRoles || {};
    const isSupplierUser =
      user && roles && user.roleId === roles.SUPPLIER ? true : false;
    const supplierUserId = user ? String(user.storeRoleValue || "") : "";
    return { isSupplierUser, supplierUserId };
  }

  componentDidMount() {
    // load suppliers then set selectedSupplierId for supplier user (if any), then fetch
    this.loadSuppliers()
      .then(() => {
        const { isSupplierUser, supplierUserId } =
          this.getSupplierUserContext();
        if (isSupplierUser && supplierUserId) {
          // force supplier id for supplier users
          this.setState({ selectedSupplierId: supplierUserId }, () =>
            this.loadAllData()
          );
        } else {
          this.loadAllData();
        }
      })
      .catch(() => {
        // still attempt to fetch even if loadSuppliers failed
        const { isSupplierUser, supplierUserId } =
          this.getSupplierUserContext();
        if (isSupplierUser && supplierUserId) {
          this.setState({ selectedSupplierId: supplierUserId }, () =>
            this.loadAllData()
          );
        } else {
          this.loadAllData();
        }
      });
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.storeId !== this.props.storeId ||
      prevProps.supplierId !== this.props.supplierId
    ) {
      this.setState({ page: 1 }, () =>
        this.loadSuppliers().finally(() => this.loadAllData())
      );
    }
  }

  // load suppliers (for display name lookup)
  loadSuppliers = async () => {
    const storeId = this.getStoreId();
    if (!storeId) return [];
    try {
      const r = await fetch(
        `${process.env.REACT_APP_API}Supplier/GetSuppliers/${storeId}`,
        { credentials: "same-origin" }
      );
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const data = await r.json();
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      this.setState({ suppliers: list });
      return list;
    } catch (err) {
      console.error("loadSuppliers error", err);
      this.setState({ suppliers: [] });
      return [];
    }
  };

  getStoreId() {
    return this.props.storeId || this.context?.storeId || "";
  }

  // Ensure URLs match your ASP.NET controllers:
  // ProductPurchaseController -> ProductPurchase/...
  // SupplierPurchaseOrderDelivered -> SupplierPurchaseOrderDelivered/...
  paymentsUrl() {
    const storeId = this.getStoreId();
    if (!storeId) return null;
    const base = (process.env.REACT_APP_API || "").replace(/\/?$/, "/");

    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();
    const supplierId =
      this.props.supplierId ||
      (isSupplierUser ? supplierUserId : this.state.selectedSupplierId) ||
      "";

    if (
      this.props.fetchUrlOverride &&
      this.props.fetchUrlOverride.trim() !== ""
    ) {
      return this.props.fetchUrlOverride;
    }

    if (supplierId && supplierId.trim() !== "") {
      return `${base}ProductPurchase/bysupplier/${storeId}/${supplierId}`;
    }
    return `${base}ProductPurchase/flat/${storeId}`;
  }

  ordersUrl() {
    const storeId = this.getStoreId();
    if (!storeId) return null;
    const base = (process.env.REACT_APP_API || "").replace(/\/?$/, "/");

    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();
    const supplierId =
      this.props.supplierId ||
      (isSupplierUser ? supplierUserId : this.state.selectedSupplierId) ||
      "";

    if (supplierId && supplierId.trim() !== "") {
      return `${base}SupplierPurchaseOrderDelivered/bysupplier/${storeId}/${supplierId}`;
    }
    return `${base}SupplierPurchaseOrderDelivered/all/${storeId}`;
  }

  // main loader: fetch payments and orders and join them
  loadAllData = async () => {
    const storeId = this.getStoreId();
    if (!storeId) {
      this.showMessage("Error", "storeId required");
      this.setState({ error: "missing storeId" });
      return;
    }
    this.setState({ loading: true, error: null });

    const token =
      this.context?.userData?.bearertoken ||
      this.context?.userData?.bearerToken;
    const headers = { Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const payUrl = this.paymentsUrl();
    const ordUrl = this.ordersUrl();
    let payments = [];
    let orders = [];
    let lastErr = null;

    // fetch payments (ProductPurchase endpoints)
    try {
      console.debug("[Payments] trying URL:", payUrl);
      const r = await fetch(payUrl, {
        method: "GET",
        credentials: "same-origin",
        headers,
      });
      console.debug(
        `[Payments] response ${r.status} ${r.statusText} for ${payUrl}`
      );
      if (!r.ok) {
        if (r.status === 404) {
          console.warn(
            "[Payments] 404 - payments controller not present, will fallback to orders data"
          );
          lastErr = lastErr || `Payments endpoint 404: ${payUrl}`;
        } else {
          const txt = await r.text().catch(() => "<no body>");
          console.warn(
            "[Payments] non-ok response",
            r.status,
            r.statusText,
            txt
          );
          lastErr = lastErr || `Payments endpoint returned ${r.status}`;
        }
      } else {
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          const txt = await r.text().catch(() => "<no body>");
          console.warn("[Payments] unexpected content-type", ct, txt);
          lastErr = lastErr || "Payments returned non-json";
        } else {
          const d = await r.json();
          payments = Array.isArray(d) ? d : d.payments || [];
          console.debug("[Payments] loaded rows:", payments.length);
        }
      }
    } catch (err) {
      console.warn("payments fetch failed", err);
      lastErr = lastErr || (err && err.message ? err.message : String(err));
    }

    // fetch orders (order-level metadata)
    try {
      console.debug("[Orders] trying URL:", ordUrl);
      const r = await fetch(ordUrl, {
        method: "GET",
        credentials: "same-origin",
        headers,
      });
      console.debug(
        `[Orders] response ${r.status} ${r.statusText} for ${ordUrl}`
      );
      if (!r.ok) {
        const txt = await r.text().catch(() => "<no body>");
        console.warn("[Orders] non-ok", r.status, r.statusText, txt);
        lastErr = lastErr || `Orders endpoint returned ${r.status}`;
      } else {
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          const txt = await r.text().catch(() => "<no body>");
          console.warn("[Orders] unexpected content-type", ct, txt);
          lastErr = lastErr || "Orders returned non-json";
        } else {
          const d = await r.json();
          orders = Array.isArray(d) ? d : d.orders || [];
          console.debug("[Orders] loaded rows:", orders.length);
        }
      }
    } catch (err) {
      console.warn("orders fetch failed", err);
      lastErr = lastErr || (err && err.message ? err.message : String(err));
    }

    // build ordersMap: purchaseId -> {orderNo, supplierId, invoiceDate}
    const ordersMap = {};
    (orders || []).forEach((o) => {
      const id = o.orderId ?? o.OrderId ?? o.id ?? null;
      if (!id) return;
      ordersMap[String(id)] = {
        orderNo: o.orderNo ?? o.OrderNo ?? o.purchaseOrderNo ?? "",
        supplierId: o.supplierId ?? o.SupplierId ?? o.supplierId,
        invoiceDate: o.invoiceDate ?? o.InvoiceDate ?? null,
        totalAmount: Number(o.totalAmount ?? o.TotalAmount ?? 0) || 0,
        paidAmount: Number(o.paidAmount ?? o.PaidAmount ?? 0) || 0,
      };
    });

    // normalize payments coming from ProductPurchase (ProdutPurchasePayment)
    const normalized = (payments || [])
      .map((p) => ({
        id: p.id ?? p.Id ?? null,
        paymentDate: p.paymentDate ?? p.PaymentDate ?? p.payment_date ?? null,
        paymentTransactionId:
          p.paymentTransactionId ??
          p.PaymentTransactionId ??
          p.paymenttransactionid ??
          p.transactionId ??
          p.TransactionId ??
          "",
        paymentMethod:
          p.paymentMethod ??
          p.PaymentMethod ??
          p.paymentmethod ??
          p.method ??
          null,
        amount: Number(p.amount ?? p.Amount ?? 0) || 0,
        notes: p.notes ?? p.Notes ?? "",
        purchaseId:
          p.purchaseId ?? p.PurchaseId ?? p.purchase_id ?? p.purchase ?? null,
        raw: p,
      }))
      .map((p) => {
        const orderMeta = p.purchaseId ? ordersMap[String(p.purchaseId)] : null;
        return {
          ...p,
          orderNo: orderMeta ? orderMeta.orderNo || "" : "",
          supplierId: orderMeta
            ? orderMeta.supplierId
            : p.raw?.supplierId ?? p.raw?.SupplierId ?? null,
        };
      });

    // If payments array is empty AND orders exist, we might want to synthesize order-level paid amounts as fallback.
    // But here we keep strictly to payments when present. If payments is empty, filtered will be empty unless synthesized:
    // (Optional) If you want to synthesize fallback rows from orders, uncomment the block below.

    if (
      (normalized.length === 0 ||
        normalized.every((p) => Number(p.amount || 0) === 0)) &&
      Object.keys(ordersMap).length
    ) {
      // synthesize simple summary rows using order-level paidAmount
      const synth = Object.keys(ordersMap).map((k) => {
        const o = ordersMap[k];
        return {
          id: k,
          paymentDate: o.invoiceDate,
          paymentTransactionId: o.orderNo || "",
          paymentMethod: null,
          amount: o.paidAmount || 0,
          notes: `Order-level paid amount (synthesized)`,
          purchaseId: k,
          orderNo: o.orderNo || "",
          supplierId: o.supplierId || null,
          synthesized: true,
        };
      });
      synth.sort((a, b) => {
        const da = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
        const db = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
        return db - da;
      });
      this.setState(
        {
          payments: synth,
          filtered: synth,
          ordersMap,
          loading: false,
          lastFetchedAt: new Date(),
          page: 1,
          error: lastErr,
        },
        () => this.applyFilters()
      );
      return;
    }

    normalized.sort((a, b) => {
      const da = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
      const db = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
      return db - da;
    });

    this.setState(
      {
        payments: normalized,
        filtered: normalized,
        ordersMap,
        loading: false,
        lastFetchedAt: new Date(),
        page: 1,
        error: lastErr,
      },
      () => this.applyFilters()
    );
  };

  // filters
  handleSearchChange = (e) =>
    this.setState({ search: e.target.value || "" }, () => this.applyFilters());
  handleFromDate = (e) =>
    this.setState({ fromDate: e.target.value || "" }, () =>
      this.applyFilters()
    );
  handleToDate = (e) =>
    this.setState({ toDate: e.target.value || "" }, () => this.applyFilters());

  applyFilters() {
    let results = (this.state.payments || []).slice();
    const { search, fromDate, toDate } = this.state;

    // If logged-in supplier, restrict to their supplier id (defense-in-depth).
    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();
    if (isSupplierUser && supplierUserId) {
      results = results.filter(
        (p) =>
          (p.supplierId || "") &&
          String(p.supplierId).toLowerCase() ===
            String(supplierUserId).toLowerCase()
      );
    }

    if (fromDate) {
      const fd = new Date(fromDate);
      results = results.filter((p) => new Date(p.paymentDate || 0) >= fd);
    }
    if (toDate) {
      const td = new Date(toDate);
      td.setHours(23, 59, 59, 999);
      results = results.filter((p) => new Date(p.paymentDate || 0) <= td);
    }

    if (search && search.trim()) {
      try {
        const fuse = new Fuse(results, {
          keys: ["paymentTransactionId", "notes", "purchaseId", "orderNo"],
          threshold: 0.25,
          ignoreLocation: true,
        });
        results = fuse.search(search).map((r) => r.item);
      } catch {
        const q = search.toLowerCase();
        results = results.filter((p) =>
          `${p.paymentTransactionId || ""} ${p.notes || ""} ${
            p.purchaseId || ""
          } ${p.orderNo || ""}`
            .toLowerCase()
            .includes(q)
        );
      }
    }

    // sort by paymentDate desc, then txn id
    results.sort((a, b) => {
      const da = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
      const db = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
      if (da !== db) return db - da;
      return (b.paymentTransactionId || "").localeCompare(
        a.paymentTransactionId || ""
      );
    });

    this.setState({ filtered: results, page: 1 });
  }

  // helper to map supplier id -> label
  getSupplierLabel = (supplierId) => {
    if (!supplierId) return "";
    const idStr = String(supplierId).toLowerCase();
    const s = (this.state.suppliers || []).find(
      (x) => (x.id || "").toString().toLowerCase() === idStr
    );
    if (!s) return supplierId;
    return s.name || s.companyName || s.emailId || s.id;
  };

  showMessage = (title, message) =>
    this.setState({
      msgboxOpen: true,
      msgboxTitle: title,
      msgboxMessage: message,
    });

  // map payment method id to readable name
  GetPaymentMethodName = (m) => {
    switch (Number(m)) {
      case 1:
        return "Cash";
      case 2:
        return "Bank Transfer";
      case 3:
        return "Cheque";
      case 4:
        return "UPI";
      case 5:
        return "Razorpay";
      default:
        return m ? String(m) : "Other";
    }
  };

  // pagination helpers
  changePage = (delta) =>
    this.setState((s) => ({ page: Math.max(1, s.page + delta) }));

  // render the simple table requested
  renderTable() {
    const { filtered, page, pageSize } = this.state;
    const start = (page - 1) * pageSize;
    const pageItems = (filtered || []).slice(start, start + pageSize);

    return (
      <div>
        <div className="overflow-auto bg-white rounded-2xl shadow p-3">
          <Table hover size="sm" responsive>
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Purchase Order</th>
                <th>Txn Date</th>
                <th>Txn Details</th>
                <th>Notes</th>
                <th>Method</th>
                <th className="text-end">Received Amount</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No payments found
                  </td>
                </tr>
              ) : (
                pageItems.map((p) => (
                  <tr key={p.id || Math.random()}>
                    <td>{this.getSupplierLabel(p.supplierId)}</td>
                    <td>{p.orderNo || p.purchaseId || ""}</td>
                    <td>
                      {p.paymentDate
                        ? new Date(p.paymentDate).toLocaleString()
                        : ""}
                    </td>
                    <td>{p.paymentTransactionId || "-"}</td>
                    <td>{p.notes || "-"}</td>

                    <td>{this.GetPaymentMethodName(p.paymentMethod)}</td>
                    <td className="text-end">
                      {Number(p.amount || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <div className="text-muted">
            Showing {pageItems.length === 0 ? 0 : start + 1} -{" "}
            {Math.min(start + pageSize, (filtered || []).length)} of{" "}
            {(filtered || []).length}
          </div>
          <div>
            <Button
              size="sm"
              className="me-1"
              onClick={() => this.changePage(-1)}
              disabled={page <= 1}
            >
              Prev
            </Button>
            <Button
              size="sm"
              onClick={() => this.changePage(1)}
              disabled={start + pageSize >= (filtered || []).length}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { loading, error, search, fromDate, toDate, lastFetchedAt } =
      this.state;
    const storeId = this.getStoreId();
    const { isSupplierUser } = this.getSupplierUserContext();

    return (
      <div className="p-3">
        {this.state.msgboxOpen && (
          <MessageBox
            open={this.state.msgboxOpen}
            title={this.state.msgboxTitle}
            message={this.state.msgboxMessage}
            callback={() => this.setState({ msgboxOpen: false })}
          />
        )}

        <Row className="align-items-center mb-3">
          <Col>
            <h4>Payments received</h4>
            <div className="text-muted small">
              Supplier:{" "}
              {isSupplierUser ? "(You)" : this.props.supplierId || "All"} •
              Store: {storeId}
            </div>
          </Col>
          <Col className="text-end">
            <ButtonToolbar>
              <Button
                size="sm"
                className="me-2"
                onClick={() => this.loadAllData()}
                disabled={loading || !storeId}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" /> &nbsp;Refreshing...
                  </>
                ) : (
                  <>
                    <BiRefresh /> &nbsp;Refresh
                  </>
                )}
              </Button>
            </ButtonToolbar>
          </Col>
        </Row>

        <Stack direction="horizontal" gap={2} className="mb-3">
          <FormControl
            placeholder="Search txn id / notes / amount"
            value={search}
            onChange={this.handleSearchChange}
            style={{ maxWidth: 360 }}
          />
          <Form.Control
            type="date"
            value={fromDate}
            onChange={this.handleFromDate}
            style={{ maxWidth: 180 }}
          />
          <Form.Control
            type="date"
            value={toDate}
            onChange={this.handleToDate}
            style={{ maxWidth: 180 }}
          />
        </Stack>

        {loading ? (
          <div className="text-center py-6">
            <Spinner animation="border" /> Loading...
          </div>
        ) : error ? (
          <div className="text-danger">{error}</div>
        ) : (
          this.renderTable()
        )}

        <div className="mt-2 small text-muted">
          {lastFetchedAt && (
            <>Last fetched: {new Date(lastFetchedAt).toLocaleString()}</>
          )}
        </div>
      </div>
    );
  }
}
